import api from './api';

export interface Client {
  id: string;
  codigoInterno: string | null;
  razaoSocial: string;
  nomeFantasia: string | null;
  cnpj: string;
  inscricaoEstadual: string | null;
  inscricaoMunicipal: string | null;
  segmento: string | null;
  prazoVencimentoDias: number;
  email: string;
  telefone: string | null;
  celular: string | null;
  site: string | null;
  contatoNome: string | null;
  contatoTelefone: string | null;
  contatoEmail: string | null;
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  estado: string | null;
  observacoes: string | null;
  active: boolean;
  createdAt: string;
  _count?: { unidades: number; documents: number; propostas: number; faturas: number };
  unidades?: Unidade[];
}

export interface Unidade {
  id: number;
  nome: string;
  cnpj: string | null;
  contatoNome: string | null;
  contatoTelefone: string | null;
  contatoEmail: string | null;
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  estado: string | null;
  active: boolean;
  clientId: string;
}

export interface TipoDocumento {
  id: number;
  nome: string;
  descricao: string | null;
  icone: string | null;
  ordem: number;
  active: boolean;
  _count?: { documents: number };
}

export interface ClientListResponse {
  data: Client[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  active: boolean;
  createdAt: string;
}

export interface UserListResponse {
  data: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ClientUser {
  id: string;
  name: string;
  email: string;
  role: string;
  active: boolean;
  clientId: string;
  createdAt: string;
  tiposPermitidos?: string; // JSON array: ["pmoc", "nf", "proposta"]
}

export interface FaturaItem {
  id: number;
  descricao: string;
  quantidade: number;
  valorUnit: number;
  subtotal: number;
  adicionalExecucao: boolean;
  tipoItem?: 'proprio' | 'terceiros' | 'revenda';
  produtoServicoId?: number;
  produtoServico?: { id: number; nome: string; codigo?: string };
}

export interface Fatura {
  id: string;
  numero: number;
  status: 'rascunho' | 'emitida' | 'enviada' | 'paga' | 'cancelada' | 'vencida';
  dataEmissao: string;
  dataVencimento: string;
  dataPagamento?: string;
  subtotal: number;
  desconto: number;
  valorDesconto: number;
  acrescimo: number;
  valorAcrescimo: number;
  valorTotal: number;
  valorPago?: number;
  formaPagamento?: string;
  dadosPagamento?: string;
  observacoes?: string;
  observacoesInternas?: string;
  visibleInPortal: boolean;
  clientId: string;
  propostaId?: string;
  client?: { id: string; razaoSocial: string; nomeFantasia?: string };
  proposta?: { id: string; numero: number };
  itens?: FaturaItem[];
  contratoId?: string;
  referenciaMes?: string;
  criadoPor?: { id: string; name: true };
  createdAt: string;
  _count?: { itens: number };
  // Centralização Financeira: documentos vinculados
  documentos?: Array<{
    id: string;
    nome: string;
    arquivo: string;
    tipoDocumento: {
      id: number;
      nome: string;
      isNf?: boolean;
    };
  }>;
}

export interface FaturaInput {
  clientId: string;
  propostaId?: string;
  dataEmissao: string;
  dataVencimento: string;
  desconto?: number;
  acrescimo?: number;
  formaPagamento?: string;
  dadosPagamento?: string;
  observacoes?: string;
  observacoesInternas?: string;
  itens: {
    produtoServicoId?: number;
    descricao: string;
    quantidade: number;
    valorUnit: number;
    adicionalExecucao?: boolean;
    tipoItem?: 'proprio' | 'terceiros' | 'revenda';
  }[];
}

export interface FaturaListResponse {
  data: Fatura[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface FaturaStats {
  total: number;
  pendentes: number;
  pagas: number;
  vencidas: number;
  valorTotal: number;
  valorRecebido: number;
}

export const adminService = {
  // Clientes
  async listClients(search?: string, page = 1): Promise<ClientListResponse> {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('page', page.toString());
    const response = await api.get<ClientListResponse>(`/admin/clients?${params}`);
    return response.data;
  },

  async getClient(id: string): Promise<Client> {
    const response = await api.get<Client>(`/admin/clients/${id}`);
    return response.data;
  },

  async createClient(data: Partial<Client> & { password: string }): Promise<Client> {
    const response = await api.post<Client>('/admin/clients', data);
    return response.data;
  },

  async updateClient(id: string, data: Partial<Client> & { password?: string }): Promise<Client> {
    const response = await api.put<Client>(`/admin/clients/${id}`, data);
    return response.data;
  },

  async deleteClient(id: string): Promise<void> {
    await api.delete(`/admin/clients/${id}`);
  },

  // Unidades
  async listUnidades(clientId: string): Promise<Unidade[]> {
    const response = await api.get<Unidade[]>(`/admin/clients/${clientId}/unidades`);
    return response.data;
  },

  async createUnidade(clientId: string, data: Partial<Unidade>): Promise<Unidade> {
    const response = await api.post<Unidade>(`/admin/clients/${clientId}/unidades`, data);
    return response.data;
  },

  async updateUnidade(id: number, data: Partial<Unidade>): Promise<Unidade> {
    const response = await api.put<Unidade>(`/admin/unidades/${id}`, data);
    return response.data;
  },

  async deleteUnidade(id: number): Promise<void> {
    await api.delete(`/admin/unidades/${id}`);
  },

  // Tipos de Documento
  async listTiposDocumento(): Promise<TipoDocumento[]> {
    const response = await api.get<TipoDocumento[]>('/admin/tipos-documento');
    return response.data;
  },

  async createTipoDocumento(data: Partial<TipoDocumento>): Promise<TipoDocumento> {
    const response = await api.post<TipoDocumento>('/admin/tipos-documento', data);
    return response.data;
  },

  async updateTipoDocumento(id: number, data: Partial<TipoDocumento>): Promise<TipoDocumento> {
    const response = await api.put<TipoDocumento>(`/admin/tipos-documento/${id}`, data);
    return response.data;
  },

  async deleteTipoDocumento(id: number): Promise<void> {
    await api.delete(`/admin/tipos-documento/${id}`);
  },

  // Upload de documentos
  async uploadDocument(formData: FormData): Promise<{ id: string; nome: string }> {
    const response = await api.post('/admin/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async deleteDocument(id: string): Promise<void> {
    await api.delete(`/admin/documents/${id}`);
  },

  async updateDocument(id: string, data: { nome: string; tipoDocumentoId: number; unidadeId: number | null; dataReferencia: string }): Promise<void> {
    await api.put(`/admin/documents/${id}`, data);
  },

  async listDocumentsByClient(clientId: string, filters?: {
    page?: number;
    limit?: number;
    tipoDocumentoId?: number;
    unidadeId?: number;
    dataInicio?: string;
    dataFim?: string;
    isNf?: boolean;
  }): Promise<{ data: any[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    const params = new URLSearchParams();
    if (clientId) params.append('clientId', clientId); // Só adiciona se tiver clientId
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.tipoDocumentoId) params.append('tipoDocumentoId', filters.tipoDocumentoId.toString());
    if (filters?.unidadeId) params.append('unidade', filters.unidadeId.toString());
    if (filters?.dataInicio) params.append('dataInicio', filters.dataInicio);
    if (filters?.dataFim) params.append('dataFim', filters.dataFim);
    if (filters?.isNf) params.append('isNf', 'true');
    const response = await api.get(`/admin/documents?${params}`);
    return response.data;
  },

  // Usuários
  async listUsers(search?: string, page = 1): Promise<UserListResponse> {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('page', page.toString());
    const response = await api.get<UserListResponse>(`/admin/users?${params}`);
    return response.data;
  },

  async getUser(id: string): Promise<User> {
    const response = await api.get<User>(`/admin/users/${id}`);
    return response.data;
  },

  async createUser(data: Partial<User> & { password: string }): Promise<User> {
    const response = await api.post<User>('/admin/users', data);
    return response.data;
  },

  async updateUser(id: string, data: Partial<User> & { password?: string }): Promise<User> {
    const response = await api.put<User>(`/admin/users/${id}`, data);
    return response.data;
  },

  async deleteUser(id: string): Promise<void> {
    await api.delete(`/admin/users/${id}`);
  },

  // Usuários de Clientes
  async listClientUsers(clientId: string): Promise<ClientUser[]> {
    const response = await api.get<ClientUser[]>(`/admin/clients/${clientId}/users`);
    return response.data;
  },

  async createClientUser(clientId: string, data: Partial<ClientUser> & { password: string }): Promise<ClientUser> {
    const response = await api.post<ClientUser>(`/admin/clients/${clientId}/users`, data);
    return response.data;
  },

  async updateClientUser(id: string, data: Partial<ClientUser> & { password?: string }): Promise<ClientUser> {
    const response = await api.put<ClientUser>(`/admin/client-users/${id}`, data);
    return response.data;
  },

  async deleteClientUser(id: string): Promise<void> {
    await api.delete(`/admin/client-users/${id}`);
  },

  // Faturas
  async listFaturas(filters?: { clientId?: string; status?: string; page?: number; limit?: number }): Promise<FaturaListResponse> {
    const params = new URLSearchParams();
    if (filters?.clientId) params.append('clientId', filters.clientId);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    const response = await api.get<FaturaListResponse>(`/faturas?${params}`);
    return response.data;
  },

  async getFatura(id: string): Promise<Fatura> {
    const response = await api.get<Fatura>(`/faturas/${id}`);
    return response.data;
  },

  async createFatura(data: FaturaInput): Promise<Fatura> {
    const response = await api.post<Fatura>('/faturas', data);
    return response.data;
  },

  async updateFatura(id: string, data: FaturaInput): Promise<Fatura> {
    const response = await api.put<Fatura>(`/faturas/${id}`, data);
    return response.data;
  },

  async updateFaturaStatus(id: string, status: string, dadosPagamento?: { valorPago?: number; dataPagamento?: string }): Promise<Fatura> {
    const response = await api.patch<Fatura>(`/faturas/${id}/status`, { status, ...dadosPagamento });
    return response.data;
  },

  async deleteFatura(id: string): Promise<void> {
    await api.delete(`/faturas/${id}`);
  },

  async downloadFaturaPdf(id: string, numero?: number): Promise<void> {
    const response = await api.get(`/faturas/${id}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `fatura-${numero ? String(numero).padStart(4, '0') : id}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  async getFaturaStats(clientId?: string): Promise<FaturaStats> {
    const params = clientId ? `?clientId=${clientId}` : '';
    const response = await api.get<FaturaStats>(`/faturas/stats${params}`);
    return response.data;
  },

  async getPropostasComFatura(): Promise<string[]> {
    const response = await api.get<string[]>('/faturas/propostas-com-fatura');
    return response.data;
  },

  async createFaturaFromContrato(contratoId: string, referenciaMes?: string): Promise<Fatura> {
    const response = await api.post<Fatura>(`/faturas/from-contrato/${contratoId}`, { referenciaMes });
    return response.data;
  },

  async gerarFaturasMensal(referenciaMes?: string): Promise<{
    referenciaMes: string;
    criadas: number;
    ignoradas: number;
    erros: { contratoId: string; titulo: string; erro: string }[];
  }> {
    const response = await api.post('/faturas/gerar-mensal', { referenciaMes });
    return response.data;
  },

  // Centralização Financeira
  async linkDocumentToFatura(documentId: string, faturaId: string | null): Promise<Document> {
    const response = await api.patch<Document>(`/admin/documents/${documentId}/link-fatura`, { faturaId });
    return response.data;
  },

  async getFaturaDocumentos(faturaId: string): Promise<Document[]> {
    const response = await api.get<Document[]>(`/faturas/${faturaId}/documentos`);
    return response.data;
  },
};
